import { useEffect, useState } from 'react';
import { Download, Maximize2, X } from 'lucide-react';
import { isSingleEventActivity, type ActivitySessionDetail } from 'shared';
import { formatSessionSpan, sessionPhase, type SessionPhase } from '@/lib/activityTime';

const PHASE_TEXT: Record<SessionPhase, string> = {
  open: 'A aceitar leituras',
  upcoming: 'Ainda não aceita leituras',
  over: 'Sessão terminada: o código já não funciona',
};

const OPEN_DAY_OVER_TEXT = 'Dia Aberto terminado: o código já não funciona';

const PHASE_STYLE: Record<SessionPhase, string> = {
  open: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
  upcoming: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
  over: 'bg-muted text-muted-foreground',
};

/** File name for the downloaded QR: the activity and the session's date. */
function qrFileName(session: ActivitySessionDetail): string {
  const slug = session.activityName
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  return `qr-${slug || 'atividade'}-${session.startsAt.slice(0, 10)}.png`;
}

interface SessionQrCardProps {
  session: ActivitySessionDetail;
}

/**
 * The code participants scan. It can be downloaded to drop into the first
 * slide of a presentation, or shown full screen straight from here.
 */
export default function SessionQrCard({ session }: SessionQrCardProps) {
  const [projecting, setProjecting] = useState(false);
  const phase = sessionPhase(session.startsAt, session.endsAt);
  const openDay = isSingleEventActivity(session.activityType);
  const what = openDay ? 'do Dia Aberto' : 'da sessão';

  useEffect(() => {
    if (!projecting) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setProjecting(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [projecting]);

  const alt = openDay
    ? `Código QR de ${session.activityName}`
    : `Código QR da sessão de ${session.activityName}`;

  return (
    <section className="card p-6 space-y-4" aria-labelledby="session-qr-heading">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h2 id="session-qr-heading" className="text-lg font-semibold text-foreground">
          Código {what}
        </h2>
        <span
          className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${PHASE_STYLE[phase]}`}
        >
          {openDay && phase === 'over' ? OPEN_DAY_OVER_TEXT : PHASE_TEXT[phase]}
        </span>
      </div>

      <div className="flex justify-center rounded-lg bg-white p-4">
        <img
          src={session.qrCode}
          alt={alt}
          className="w-64 h-64"
          style={{ imageRendering: 'pixelated' }}
        />
      </div>

      <p className="text-sm text-muted-foreground">
        {openDay
          ? 'Quem ler este código fica presente no Dia Aberto e faz check-in no centro. É o mesmo código do princípio ao fim, e não é preciso ler o código de cada equipamento. Só funciona desde 30 minutos antes do início até ao fim do Dia Aberto.'
          : 'Quem ler este código fica presente na sessão e faz check-in no centro. Não precisa de ler o código de cada equipamento. Só funciona desde 30 minutos antes do início até ao fim da sessão.'}
      </p>

      <div className="flex items-center gap-2 flex-wrap">
        <button
          type="button"
          onClick={() => setProjecting(true)}
          className="flex items-center gap-2 px-4 py-2 rounded-md bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 transition-colors"
        >
          <Maximize2 className="w-4 h-4" />
          Mostrar em ecrã inteiro
        </button>
        <a
          href={session.qrCode}
          download={qrFileName(session)}
          className="flex items-center gap-2 px-4 py-2 rounded-md border border-border bg-card text-sm font-medium text-foreground hover:bg-accent transition-colors"
        >
          <Download className="w-4 h-4" />
          Descarregar para o slide
        </a>
      </div>

      {projecting && (
        <div
          className="fixed inset-0 z-50 flex flex-col items-center justify-center gap-6 bg-white p-8 text-black"
          role="dialog"
          aria-modal="true"
          aria-label={`Código ${what} em ecrã inteiro`}
        >
          <button
            type="button"
            onClick={() => setProjecting(false)}
            aria-label="Fechar ecrã inteiro"
            className="absolute top-4 right-4 p-2 rounded-md text-black/60 hover:text-black hover:bg-black/5 transition-colors"
            autoFocus
          >
            <X className="w-6 h-6" />
          </button>
          <div className="text-center">
            <p className="text-4xl font-bold">{session.activityName}</p>
            <p className="mt-2 text-xl text-black/70">
              {formatSessionSpan(session.startsAt, session.endsAt)} · {session.leaderName}
            </p>
          </div>
          <img
            src={session.qrCode}
            alt={alt}
            style={{ width: 'min(60vh, 80vw)', height: 'min(60vh, 80vw)', imageRendering: 'pixelated' }}
          />
          <p className="text-2xl font-medium">Lê o código com a app DIGGER para entrar</p>
        </div>
      )}
    </section>
  );
}
